import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import toast from "react-hot-toast";
import api from "../../services/api";
import Loader from "../../components/loader";

const ClientView = () => {
  const { id } = useParams();
  const [budget, setBudget] = useState(null);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchBudget = async () => {
    try {
      const { ok, data } = await api.get(`/budget/public/${id}`);
      if (!ok) return toast.error("Failed to fetch budget");
      setBudget(data);
    } catch (e) {
      toast.error(e.message || "Failed to fetch budget");
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const fetchActivities = async () => {
    try {
      const { ok, data } = await api.post(`/activity/public/search`, { budgetId: id });
      if (!ok) return toast.error("Failed to fetch activities");
      setActivities(data);
    } catch (e) {
      toast.error(e.message || "Failed to fetch activities");
      console.error(e);
    }
  };

  useEffect(() => {
    fetchBudget();
    fetchActivities();
  }, [id]);

  if (loading)
    return (
      <div className="bg-gray-100 min-h-screen flex items-center justify-center">
        <Loader />
      </div>
    );

  if (!budget)
    return (
      <div className="bg-gray-100 min-h-screen flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-md p-8 text-center text-gray-600">Budget not found.</div>
      </div>
    );

  return (
    <div className="bg-gray-100 min-h-screen p-4 sm:p-8">
      <div className="max-w-5xl mx-auto space-y-8">
        <div className="bg-white rounded-lg shadow-md p-6 flex items-center gap-4">
          {budget.projectLogo && <img src={budget.projectLogo} alt={budget.projectName} className="w-12 h-12 rounded-md bg-gray-100 p-1 object-contain" />}
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">{budget.projectName}</h1>
            <p className="text-gray-500 text-sm">{budget.name}</p>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6 space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">{budget.name}</h2>
              {budget.startAt && budget.endAt && (
                <p className="text-gray-500 text-sm">
                  {new Date(budget.startAt).toLocaleDateString("fr-FR")} - {new Date(budget.endAt).toLocaleDateString("fr-FR")}
                </p>
              )}
            </div>
            {!budget.signed_at && <span className="text-xs font-medium text-orange-700 bg-orange-100 px-2 py-1 rounded-full self-start">Not signed yet</span>}
          </div>

          <div>
            <div className="flex justify-between text-sm text-gray-600 mb-1">
              <span>Budget consumed</span>
              <span>
                {(budget.amountUsed || 0).toLocaleString("fr-FR", { style: "currency", currency: "EUR" })} /{" "}
                {(budget.amount || 0).toLocaleString("fr-FR", { style: "currency", currency: "EUR" })}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div className="bg-sky-500 h-2 rounded-full" style={{ width: `${Math.min(budget.amount ? Math.round(((budget.amountUsed || 0) / budget.amount) * 100) : 0, 100)}%` }} />
            </div>
          </div>

          {!activities.length ? (
            <p className="text-gray-500 text-sm">No hours logged on this budget yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 border-b border-gray-200">
                    <th className="py-2 pr-4">Member</th>
                    <th className="py-2 pr-4">Month</th>
                    <th className="py-2 pr-4">Hours</th>
                    <th className="py-2 pr-4">Daily breakdown</th>
                    <th className="py-2 pr-4">TJM</th>
                    <th className="py-2">Value</th>
                  </tr>
                </thead>
                <tbody>
                  {activities.map((activity) => (
                    <tr key={activity._id} className="border-b border-gray-100 align-top">
                      <td className="py-2 pr-4 flex items-center gap-2 whitespace-nowrap">
                        {activity.userAvatar && <img src={activity.userAvatar} alt={activity.userName} className="w-6 h-6 rounded-full" />}
                        {activity.userName}
                      </td>
                      <td className="py-2 pr-4 whitespace-nowrap">{new Date(activity.date).toLocaleDateString("fr-FR", { month: "long", year: "numeric" })}</td>
                      <td className="py-2 pr-4 whitespace-nowrap">{activity.total}h</td>
                      <td className="py-2 pr-4 text-gray-500">{getDailyBreakdown(activity)}</td>
                      <td className="py-2 pr-4 whitespace-nowrap">{(activity.userTjms || 0).toLocaleString("fr-FR", { style: "currency", currency: "EUR" })}</td>
                      <td className="py-2 whitespace-nowrap">{(activity.value || 0).toLocaleString("fr-FR", { style: "currency", currency: "EUR" })}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const getDailyBreakdown = (activity) => {
  const month = new Date(activity.date).getMonth() + 1;
  const daysWorked = (activity.detail || []).map((day, index) => ({ dayOfMonth: index + 1, value: day.value })).filter((day) => day.value > 0);
  if (!daysWorked.length) return "-";
  return daysWorked.map((day) => `${day.dayOfMonth}/${month}: ${day.value}h`).join(", ");
};

export default ClientView;
